/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/

import * as actions from ".";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { Organization, LoadStatus, Edits, UserRole } from "../../../types";

export interface UseWithOrganizations {
  organizations: Record<string, Organization>;
  organizationEdits: Record<string, Edits<Organization>>;
  organizationLoadStatus: LoadStatus;
  loadAllOrganizations: () => Promise<Organization[]>;
  createOrganization: (newData: Partial<Organization>) => Promise<Organization>;
  deleteOrganization: (organization: Organization) => Promise<void>;
  updateOrganization: (newData: Partial<Organization>) => Promise<Organization>;
}

export default function useWithOrganizations(): UseWithOrganizations {
  const { user } = useAppSelector((state) => state.login);
  const { organizations, organizationEdits, organizationLoadStatus } =
    useAppSelector((state) => state.organizations);
  const dispatch = useAppDispatch();

  async function loadAllOrganizations(): Promise<Organization[]> {
    if (organizationLoadStatus === LoadStatus.IN_PROGRESS) return [];
    return await dispatch(actions.loadOrganizations()).unwrap();
  }

  async function createOrganization(
    data: Partial<Organization>
  ): Promise<Organization> {
    if (!user) throw new Error("login required");
    if (!data.name) throw new Error("name required");
    if (!data.accessCode) throw new Error("accessCode required");
    if (
      Object.values(organizations).find(
        (s) => s.accessCode === data.accessCode && s.id !== data.id
      )
    ) {
      throw new Error("code already in use");
    }
    return await dispatch(
      actions.addOrUpdateOrganization({
        id: data.id,
        name: data.name || "",
        desc: data.desc || "",
        hidden: Boolean(data.hidden),
        deleted: Boolean(data.deleted),
        accessCode: data.accessCode,
        members: [{ user: user.id, role: UserRole.ADMIN }],
        config: data.config,
      })
    ).unwrap();
  }

  async function deleteOrganization(organization: Organization): Promise<void> {
    await dispatch(actions.deleteOrganization(organization.id));
  }

  async function updateOrganization(
    data: Partial<Organization>
  ): Promise<Organization> {
    return await dispatch(actions.addOrUpdateOrganization(data)).unwrap();
  }

  return {
    organizations,
    organizationEdits,
    organizationLoadStatus,
    loadAllOrganizations,
    createOrganization,
    deleteOrganization,
    updateOrganization,
  };
}
